import type Konva from "konva";
import React from "react";
import { Shape } from "react-konva";
import { useMapEditorUI } from "../../contexts/MapEditorContext";
import {
  EMPTY_ZONE_CHECKS,
  type ZoneChecks,
  ZoneChecksWatcher
} from "../../hooks/useZoneChecks";

const CONFLICT_COLOR = "#dc2626";
const UNTAGGED_COLOR = "#d97706";
const UNPLACED_COLOR = "#dc2626";
/** Gap between the dot's edge and the ring around it. */
const UNPLACED_MARGIN = 6;

interface ZoneChecksMarksProps {
  checks: ZoneChecks;
}

/**
 * All marks of one kind are drawn by a single Konva node with a custom
 * scene function. A floor can have hundreds of unplaced desks; one node per
 * desk made every layer redraw and every React render of this overlay pay
 * for each of them.
 */
const ZoneChecksMarks = React.memo<ZoneChecksMarksProps>(({ checks }) => {
  const drawUntagged = React.useCallback(
    (ctx: Konva.Context, node: Konva.Shape) => {
      ctx.beginPath();
      for (const region of checks.untagged) {
        ctx.rect(
          region.x - 3,
          region.y - 3,
          region.width + 6,
          region.height + 6
        );
      }
      ctx.strokeShape(node);
    },
    [checks.untagged]
  );
  const drawConflicting = React.useCallback(
    (ctx: Konva.Context, node: Konva.Shape) => {
      ctx.beginPath();
      for (const region of checks.conflicting) {
        ctx.rect(
          region.x - 4,
          region.y - 4,
          region.width + 8,
          region.height + 8
        );
      }
      ctx.strokeShape(node);
    },
    [checks.conflicting]
  );
  const drawUnplaced = React.useCallback(
    (ctx: Konva.Context, node: Konva.Shape) => {
      ctx.beginPath();
      for (const spot of checks.unplaced) {
        if (spot.hidesDot && spot.areaBounds) {
          // No dot to ring: the highlight area is the spot's indicator.
          ctx.rect(
            spot.areaBounds.x - UNPLACED_MARGIN,
            spot.areaBounds.y - UNPLACED_MARGIN,
            spot.areaBounds.width + UNPLACED_MARGIN * 2,
            spot.areaBounds.height + UNPLACED_MARGIN * 2
          );
          continue;
        }
        const radius = spot.dotRadius + UNPLACED_MARGIN;
        ctx.moveTo(spot.dotX + radius, spot.dotY);
        ctx.arc(spot.dotX, spot.dotY, radius, 0, Math.PI * 2);
      }
      ctx.strokeShape(node);
    },
    [checks.unplaced]
  );

  return (
    <>
      {checks.untagged.length > 0 ? (
        <Shape
          sceneFunc={drawUntagged}
          stroke={UNTAGGED_COLOR}
          strokeWidth={2}
          dash={[6, 4]}
          listening={false}
          perfectDrawEnabled={false}
        />
      ) : null}
      {checks.conflicting.length > 0 ? (
        <Shape
          sceneFunc={drawConflicting}
          stroke={CONFLICT_COLOR}
          strokeWidth={3}
          dash={[10, 5]}
          listening={false}
          perfectDrawEnabled={false}
        />
      ) : null}
      {checks.unplaced.length > 0 ? (
        <Shape
          sceneFunc={drawUnplaced}
          stroke={UNPLACED_COLOR}
          strokeWidth={2}
          dash={[4, 3]}
          listening={false}
          perfectDrawEnabled={false}
        />
      ) : null}
    </>
  );
});
ZoneChecksMarks.displayName = "ZoneChecksMarks";

/**
 * Draws the zone checks on the editing canvas: regions that overlap without
 * nesting get a red dashed outline, open areas without a zone an amber one,
 * and desks that sit in no space a red ring. Nothing is drawn when the map
 * is clean. Toggled from the Zones panel. The marks only re-render when the
 * checks change, not on every editor UI state change.
 */
export const ZoneChecksOverlay: React.FC = () => {
  const { showZoneChecks } = useMapEditorUI();
  const [checks, setChecks] = React.useState<ZoneChecks>(EMPTY_ZONE_CHECKS);

  if (!showZoneChecks) return null;

  return (
    <>
      <ZoneChecksWatcher onChange={setChecks} />
      <ZoneChecksMarks checks={checks} />
    </>
  );
};
